import { FRONTEND_SERVER, SERVER_URL } from '../../../config';

export type TNotificationUserType = 'agent' | 'client' | string;

export type TNotificationSetting = {
    color: string;
    url: string | null;
    pkName?: string;
};

export type TCategoryGroup = {
    shiftManagementIssues?: string[];
    trackingIssues?: string[];
    freelancerIssues?: string[];
    jobsIssues?: string[];
    operationalIssues?: string[];
    clockingIssues: string[];
    feedbackIssues?: string[];
};

export type TNotificationSettingsMap = {
    freelancer_invitation: TNotificationSetting;
    freelancer_published: TNotificationSetting;
    freelancer_booked: TNotificationSetting;
    freelancer: TNotificationSetting;
    client_booked_onefreelancer: TNotificationSetting;
    client_job_request_changed: TNotificationSetting;
    client_feedback_request: TNotificationSetting;
    booking_cancelled: TNotificationSetting;
    admin_push_message: TNotificationSetting;
    client_reminder: TNotificationSetting;
    freelancer_reminder: TNotificationSetting;
    'shiftmanagement.jobnotfilled': TNotificationSetting;
    'shiftmanagement.jobdelegated': TNotificationSetting;
    'shiftmanagement.backupnotused': TNotificationSetting;
    'shiftmanagement.bookingautomatched': TNotificationSetting;
    'shiftmanagement.booking_self_cancelled': TNotificationSetting;
    'tracking_issue.late': TNotificationSetting;
    'tracking_issue.no_gps': TNotificationSetting;
    'tracking_issue.device_offline': TNotificationSetting;
    'tracking_issue.gps_disabled': TNotificationSetting;
    'clocking_issue.clockin_late': TNotificationSetting;
    'clocking_issue.clockin_far': TNotificationSetting;
    'clocking_issue.clockin_missing': TNotificationSetting;
    'clocking_issue.clockout_late': TNotificationSetting;
    bad_feedback: TNotificationSetting;
    freelancer_request_departure_confirmation: TNotificationSetting;
    upcoming_interview: TNotificationSetting;
    'freelancer.dvla_check_expiring': TNotificationSetting;
    'freelancer.booking_removed': TNotificationSetting;
    'freelancer.available_to_neighbouring_offices': TNotificationSetting;
    freelancers_replaced: TNotificationSetting;
    'client_shiftmanagement.job_cancelled': TNotificationSetting;
    'client_shiftmanagement.job_changed': TNotificationSetting;
    client_job_request_confirmed: TNotificationSetting;
    'client_tracking_issue.lateness': TNotificationSetting;
    'client_clocking.clockout_confirm': TNotificationSetting;
    'client_feedbacks.missing_feedback': TNotificationSetting;
};

export const AGENT_NOTIFICATION_CATEGORIES: TCategoryGroup = {
    shiftManagementIssues: [
        'shiftmanagement.jobdelegated',
        'shiftmanagement.jobnotfilled',
        'shiftmanagement.backupnotused',
        'shiftmanagement.bookingautomatched',
        'shiftmanagement.booking_self_cancelled',
    ],
    trackingIssues: [
        'tracking_issue.gps_disabled',
        'tracking_issue.late',
        'tracking_issue.device_offline',
        'freelancer.booking_removed',
    ],
    freelancerIssues: [
        'freelancer',
        'bad_feedback',
        'freelancer.available_to_neighbouring_offices',
        'freelancer.dvla_check_expiring',
        'upcoming_interview',
    ],
    clockingIssues: [
        'clocking_issue.clockin_late',
        'clocking_issue.clockin_far',
        'clocking_issue.clockout_late',
        'clocking_issue.clockin_missing',
    ],
};

export const CLIENT_NOTIFICATION_CATEGORIES: TCategoryGroup = {
    jobsIssues: [
        'freelancers_replaced',
        'client_shiftmanagement.job_cancelled',
        'client_shiftmanagement.job_changed',
        'client_job_request_confirmed',
    ],
    operationalIssues: ['client_tracking_issue.lateness'],
    clockingIssues: ['client_clocking.clockout_confirm'],
    feedbackIssues: ['client_feedbacks.missing_feedback'],
};

export const getNotificationCounterCategoryQuery = (
    userType: TNotificationUserType,
): string[] => {
    if (userType === 'agent') {
        return [
            ...AGENT_NOTIFICATION_CATEGORIES.shiftManagementIssues,
            ...AGENT_NOTIFICATION_CATEGORIES.trackingIssues,
            ...AGENT_NOTIFICATION_CATEGORIES.freelancerIssues,
            ...AGENT_NOTIFICATION_CATEGORIES.clockingIssues,
        ];
    }
    if (userType === 'client') {
        return [
            ...CLIENT_NOTIFICATION_CATEGORIES.jobsIssues,
            ...CLIENT_NOTIFICATION_CATEGORIES.operationalIssues,
            ...CLIENT_NOTIFICATION_CATEGORIES.clockingIssues,
            ...CLIENT_NOTIFICATION_CATEGORIES.feedbackIssues,
        ];
    }
    return [];
};

export const getNotificationCounterQuery = (userType: TNotificationUserType): string =>
    getNotificationCounterCategoryQuery(userType).length
        ? `is_read=0&case_status=OP&category=${getNotificationCounterCategoryQuery(
              userType,
          ).join('&category=')}`
        : 'is_read=0';

export const uiCategorySettings: TNotificationSettingsMap = {
    freelancer_invitation: {
        color: '#1ebf1e',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    freelancer_published: {
        color: '#387FFB',
        url: `${SERVER_URL}/freelancer/%s/`,
        pkName: 'object_id',
    },
    freelancer_booked: {
        color: '#387FFB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    freelancer: {
        color: '#387FFB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    client_booked_onefreelancer: {
        color: '#008000',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    client_job_request_changed: {
        color: '#f49e42',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    client_feedback_request: {
        color: '#666699',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    booking_cancelled: {
        color: '#999999',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    admin_push_message: {
        color: '#c122d6',
        url: null,
    },
    client_reminder: {
        color: '#c122d6',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    freelancer_reminder: {
        color: '#c122d6',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    'shiftmanagement.jobnotfilled': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    'shiftmanagement.jobdelegated': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    'shiftmanagement.backupnotused': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'additional_object_id',
    },
    'shiftmanagement.bookingautomatched': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'additional_object_id',
    },
    'shiftmanagement.booking_self_cancelled': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'additional_object_id',
    },
    'tracking_issue.late': {
        color: '#ff0000',
        url: `${FRONTEND_SERVER}/operations/locationtracking/map/%s/`,
        pkName: 'object_id',
    },
    'tracking_issue.no_gps': {
        color: '#ff0000',
        url: `${FRONTEND_SERVER}/operations/locationtracking/map/%s/`,
        pkName: 'object_id',
    },
    'tracking_issue.device_offline': {
        color: '#ff0000',
        url: `${FRONTEND_SERVER}/operations/locationtracking/map/%s/`,
        pkName: 'object_id',
    },
    'tracking_issue.gps_disabled': {
        color: '#ff0000',
        url: `${FRONTEND_SERVER}/operations/locationtracking/map/%s/`,
        pkName: 'object_id',
    },
    'clocking_issue.clockin_late': {
        color: '#ffa64d',
        url: `${FRONTEND_SERVER}/operations/bookings/expanded/%s/clockin-out/%s?search=BK%s&getall=1`,
        pkName: 'object_id',
    },
    'clocking_issue.clockin_far': {
        color: '#ffa64d',
        url: `${FRONTEND_SERVER}/operations/bookings/expanded/%s/clockin-out/%s?search=BK%s&getall=1`,
        pkName: 'object_id',
    },
    'clocking_issue.clockin_missing': {
        color: '#ffa64d',
        url: `${FRONTEND_SERVER}/operations/bookings/expanded/%s/clockin-out/%s?search=BK%s&getall=1`,
        pkName: 'object_id',
    },
    'clocking_issue.clockout_late': {
        color: '#ffa64d',
        url: `${FRONTEND_SERVER}/operations/bookings/expanded/%s/clockin-out/%s?search=BK%s&getall=1`,
        pkName: 'object_id',
    },
    bad_feedback: {
        color: '#2eb82e',
        url: `${FRONTEND_SERVER}/operations/bookings/expanded/%s/?search=BK%s&getall=1`,
        pkName: 'object_id',
    },
    freelancer_request_departure_confirmation: {
        color: '#2eb82e',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    upcoming_interview: {
        color: '#2eb82e',
        url: `${SERVER_URL}/dashboard/admin/induction-session-calendar/%s/`,
        pkName: 'object_id',
    },
    'freelancer.dvla_check_expiring': {
        color: '#2eb82e',
        url: `${SERVER_URL}/freelancer/admin/documents/?type=DV`,
    },
    'freelancer.booking_removed': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    'freelancer.available_to_neighbouring_offices': {
        color: '#2C7CBB',
        url: `${FRONTEND_SERVER}/operations/freelancer/%s/`,
        pkName: 'object_id',
    },
    freelancers_replaced: {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    'client_shiftmanagement.job_cancelled': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    'client_shiftmanagement.job_changed': {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    client_job_request_confirmed: {
        color: '#2C7CBB',
        url: `${SERVER_URL}/job/%s/`,
        pkName: 'object_id',
    },
    'client_tracking_issue.lateness': {
        color: '#ff0000',
        url: `${FRONTEND_SERVER}/operations/locationtracking/map/%s/`,
        pkName: 'object_id',
    },
    'client_clocking.clockout_confirm': {
        color: '#ffa64d',
        url: `${FRONTEND_SERVER}/operations/bookings/expanded/%s/clockin-out/%s?search=BK%s&getall=1`,
        pkName: 'object_id',
    },
    'client_feedbacks.missing_feedback': {
        color: '#666699',
        url: `${FRONTEND_SERVER}/operations/bookings/expanded/%s/?getall=1`,
        pkName: 'object_id',
    },
};
